import { useState, useCallback, useRef, useEffect } from 'react';
import {
  DeckCard,
  ScoreBreakdown,
  TurnResolution,
  JokerDefinition,
  JokerEffect,
  ShopOffer,
  RunShopState,
} from '@solante/shared';
import {
  generateDeckOrder,
  resolveCombo,
  calculateScore,
  JOKER_POOL,
  describeJokerEffect,
} from '@solante/shared';
import { ClientMatchState } from '../state/roomStore';
import { TUTORIAL_PLAYER_WALLET, TUTORIAL_AI_WALLET } from '../utils/adaptPracticeState';
import { useTutorialMatchStore } from '../state/tutorialMatchStore';
import { soundManager } from '../utils/sounds';

// ── Types ────────────────────────────────────────────────────────────────────

export type TutorialPhase =
  | 'welcome'
  | 'deal_hand'
  | 'guide_select'
  | 'await_play'
  | 'show_score'
  | 'ai_thinking'
  | 'ai_scored'
  | 'explain_hearts'
  | 'shop_intro'
  | 'shop_active'
  | 'shop_done'
  | 'explain_joker'
  | 'victory';

type CoachCategory = 'gameplay' | 'scoring' | 'shop' | 'combat' | 'system';

export interface CoachConfig {
  title: string;
  body: string;
  category: CoachCategory;
  highlightSelector?: string;
  requiredAction?: string;
  dismissLabel?: string;
}

interface BestPlay {
  cards: DeckCard[];
  breakdown: ScoreBreakdown;
}

const TARGET_SCORE = 500;
const STARTING_CHIPS = 320;
const STARTING_HEARTS = 3;
const HAND_SIZE = 6;
const SHOP_AFTER_HANDS = 2;

// ── findBestPlay ─────────────────────────────────────────────────────────────

type Modifier = { name: string; bonus: number };

function getSubsets(hand: DeckCard[], minSize: number, maxSize: number): DeckCard[][] {
  const result: DeckCard[][] = [];
  const n = hand.length;
  // Enumerate all bitmasks for subsets of size minSize..maxSize
  for (let mask = 1; mask < 1 << n; mask++) {
    const bits = popcount(mask);
    if (bits < minSize || bits > maxSize) continue;
    const subset: DeckCard[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) subset.push(hand[i]);
    }
    result.push(subset);
  }
  return result;
}

function popcount(n: number): number {
  let count = 0;
  while (n) {
    count += n & 1;
    n >>= 1;
  }
  return count;
}

export function findBestPlay(hand: DeckCard[], modifiers: Modifier[] = []): BestPlay {
  const subsets = getSubsets(hand, 1, 5);
  let best: BestPlay | null = null;
  for (const subset of subsets) {
    const breakdown = calculateScore(subset, modifiers);
    if (!best || breakdown.total > best.breakdown.total) {
      best = { cards: subset, breakdown };
    }
  }
  // Fallback — should never happen since hand has at least 1 card
  return best ?? { cards: [hand[0]], breakdown: calculateScore([hand[0]], modifiers) };
}

// ── Joker → Modifier conversion ──────────────────────────────────────────────

function jokerModifiers(jokers: JokerDefinition[], combo: string, hearts: number): Modifier[] {
  const mods: Modifier[] = [];
  for (const joker of jokers) {
    for (const eff of joker.effects) {
      switch (eff.kind) {
        case 'flatScore':
          mods.push({ name: joker.name, bonus: eff.amount });
          break;
        case 'handTypeFlat':
          if (eff.hand === combo) mods.push({ name: joker.name, bonus: eff.amount });
          break;
        case 'scorePerHeart':
          mods.push({ name: joker.name, bonus: Math.round(eff.percentPerHeart * hearts * 100) });
          break;
        // percentScore and handTypePercent are multiplicative — approximate as flat bonus
        // for simplicity in tutorial context
        default:
          break;
      }
    }
  }
  return mods;
}

// ── Shop generation ──────────────────────────────────────────────────────────

function generateShopOffers(seed: number): ShopOffer[] {
  const eligible = JOKER_POOL.filter((j) => j.cost > 0 && j.cost <= 150 && j.rarity === 'common');
  // Simple seeded shuffle to pick 3
  const shuffled = [...eligible].sort((a, b) => {
    const ha = hashStr(a.id + seed) & 0xffff;
    const hb = hashStr(b.id + seed) & 0xffff;
    return ha - hb;
  });
  return shuffled.slice(0, 3).map((joker, i) => ({
    kind: 'joker' as const,
    offerId: `shop-${i}-${joker.id}`,
    joker,
  }));
}

function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

// ── Card name formatting ─────────────────────────────────────────────────────

function cardName(c: DeckCard): string {
  return `${c.rank} of ${c.suit}`;
}

function cardListStr(cards: DeckCard[]): string {
  return cards.map(cardName).join(', ');
}

// ── Engine State ─────────────────────────────────────────────────────────────

interface EngineState {
  phase: TutorialPhase;
  deck: DeckCard[];
  deckIndex: number;
  currentHand: DeckCard[];
  bestPlay: BestPlay | null;
  selectedCards: string[];
  handSequence: number;
  playerScore: number;
  aiScore: number;
  playerHearts: number;
  playerChips: number;
  playerJokers: JokerDefinition[];
  timeline: TurnResolution[];
  handsPlayed: number;
  shopVisited: boolean;
  heartsExplained: boolean;
  jokerExplained: boolean;
  isCompleted: boolean;
  winner: string | null;
  shopOffers: ShopOffer[];
  lastPlayerBreakdown: ScoreBreakdown | null;
  lastAiBreakdown: ScoreBreakdown | null;
  turnNumber: number;
}

function createInitialState(): EngineState {
  const seed = crypto.randomUUID().replace(/-/g, '');
  const deck = generateDeckOrder(seed, 'tutorial-client', 0);
  return {
    phase: 'welcome',
    deck,
    deckIndex: 0,
    currentHand: [],
    bestPlay: null,
    selectedCards: [],
    handSequence: 0,
    playerScore: 0,
    aiScore: 0,
    playerHearts: STARTING_HEARTS,
    playerChips: STARTING_CHIPS,
    playerJokers: [],
    timeline: [],
    handsPlayed: 0,
    shopVisited: false,
    heartsExplained: false,
    jokerExplained: false,
    isCompleted: false,
    winner: null,
    shopOffers: [],
    lastPlayerBreakdown: null,
    lastAiBreakdown: null,
    turnNumber: 1,
  };
}

// ── Engine Return Type ───────────────────────────────────────────────────────

export interface TutorialEngine {
  state: EngineState;
  currentPhase: TutorialPhase;
  coachConfig: CoachConfig | null;
  showCoach: boolean;
  coachActionCompleted: boolean;
  clientMatchState: ClientMatchState;
  isMyTurn: boolean;
  isShopPhase: boolean;
  selectedCards: string[];
  handleCardClick: (cardId: string) => void;
  handlePlayHand: (cardIds: string[], powerCardIds: string[]) => void;
  handleDismiss: () => void;
  handleSkipAll: () => void;
  handlePurchase: (offerId: string) => Promise<void>;
  handleShopReady: () => Promise<void>;
  handlePlayAgain: () => void;
  handleSelectionChange: (ids: string[]) => void;
}

// ── The Hook ─────────────────────────────────────────────────────────────────

export function useTutorialEngine(): TutorialEngine {
  const [engineState, setEngineState] = useState<EngineState>(createInitialState);
  const [coachDismissedForPhase, setCoachDismissedForPhase] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const { tipsSkipped, skipAllTips, resetTips, markMechanicLearned, incrementCompleted } =
    useTutorialMatchStore();

  // Reset tips on mount so every tutorial match starts with coaching enabled
  useEffect(() => {
    resetTips();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset coach dismissed state when phase changes
  useEffect(() => {
    setCoachDismissedForPhase(null);
  }, [engineState.phase]);

  // Clean up timers
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // ── Deal a hand to the player ──────────────────────────────────────────────

  const dealHand = useCallback((prev: EngineState): EngineState => {
    const hand: DeckCard[] = [];
    let idx = prev.deckIndex;
    for (let i = 0; i < HAND_SIZE; i++) {
      // Wrap around if deck exhausted
      hand.push(prev.deck[idx % prev.deck.length]);
      idx++;
    }
    const mods = jokerModifiers(prev.playerJokers, '', prev.playerHearts);
    const best = findBestPlay(hand, mods);
    return {
      ...prev,
      currentHand: hand,
      deckIndex: idx,
      bestPlay: best,
      selectedCards: [],
      handSequence: prev.handSequence + 1,
      phase: 'guide_select',
    };
  }, []);

  // ── AI turn simulation ─────────────────────────────────────────────────────

  const simulateAiTurn = useCallback((prev: EngineState): EngineState => {
    // AI gets 2 cards → usually Exit Liquidity or Twin Rugs
    const aiCards: DeckCard[] = [];
    let idx = prev.deckIndex;
    for (let i = 0; i < 2; i++) {
      aiCards.push(prev.deck[idx % prev.deck.length]);
      idx++;
    }
    const aiBreakdown = calculateScore(aiCards);
    const newAiScore = prev.aiScore + aiBreakdown.total;
    const aiTurn: TurnResolution = {
      sequence: prev.timeline.length + 1,
      player: TUTORIAL_AI_WALLET,
      cardsPlayed: aiCards,
      modifiers: [],
      breakdown: aiBreakdown,
    };
    return {
      ...prev,
      deckIndex: idx,
      aiScore: newAiScore,
      timeline: [...prev.timeline, aiTurn],
      lastAiBreakdown: aiBreakdown,
      phase: 'ai_scored',
      turnNumber: prev.turnNumber + 1,
    };
  }, []);

  // ── Phase transition after ai_scored ────────────────────────────────────────

  const afterAiScored = useCallback(
    (prev: EngineState): EngineState => {
      // Check if we need hearts explanation
      if (prev.handsPlayed === 1 && !prev.heartsExplained) {
        markMechanicLearned('hearts');
        return { ...prev, phase: 'explain_hearts', heartsExplained: true };
      }
      // Check if we need shop
      if (prev.handsPlayed >= SHOP_AFTER_HANDS && !prev.shopVisited) {
        markMechanicLearned('shop');
        const offers = generateShopOffers(prev.handSequence);
        return {
          ...prev,
          phase: 'shop_intro',
          shopVisited: true,
          shopOffers: offers,
        };
      }
      // Otherwise deal next hand
      return dealHand(prev);
    },
    [dealHand, markMechanicLearned],
  );

  // ── Auto-advance for ai_thinking phase ──────────────────────────────────────

  useEffect(() => {
    if (engineState.phase === 'ai_thinking') {
      timerRef.current = setTimeout(() => {
        setEngineState((prev) => simulateAiTurn(prev));
      }, 2000);
    }
    if (engineState.phase === 'deal_hand') {
      // Instant deal
      setEngineState((prev) => dealHand(prev));
    }
    if (engineState.phase === 'shop_done') {
      // Auto-advance to next deal
      setEngineState((prev) => dealHand(prev));
    }
  }, [engineState.phase, dealHand, simulateAiTurn]);

  // ── Auto-skip coach-only phases when tips are skipped ────────────────────────

  useEffect(() => {
    if (!tipsSkipped) return;

    const phase = engineState.phase;
    const coachOnlyPhases: TutorialPhase[] = [
      'welcome',
      'show_score',
      'ai_scored',
      'explain_hearts',
      'shop_intro',
      'explain_joker',
    ];
    if (!coachOnlyPhases.includes(phase)) return;

    // Small delay to avoid synchronous state cascades
    const timer = setTimeout(() => {
      setEngineState((prev) => {
        switch (prev.phase) {
          case 'welcome':
            return dealHand(prev);
          case 'show_score':
            return { ...prev, phase: 'ai_thinking' as TutorialPhase };
          case 'ai_scored':
            return afterAiScored(prev);
          case 'explain_hearts':
            return dealHand(prev);
          case 'shop_intro':
            return { ...prev, phase: 'shop_active' as TutorialPhase };
          case 'explain_joker':
            return { ...prev, phase: 'shop_active' as TutorialPhase };
          default:
            return prev;
        }
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [engineState.phase, tipsSkipped, dealHand, afterAiScored]);

  // ── Card click handler ──────────────────────────────────────────────────────

  const handleCardClick = useCallback(
    (cardId: string) => {
      if (engineState.phase !== 'guide_select' && engineState.phase !== 'await_play') return;

      setEngineState((prev) => {
        const isSelected = prev.selectedCards.includes(cardId);
        let newSelected: string[];
        if (isSelected) {
          soundManager.play('click');
          newSelected = prev.selectedCards.filter((id) => id !== cardId);
        } else {
          if (prev.selectedCards.length >= 5) {
            soundManager.play('error');
            return prev;
          }
          soundManager.play('select');
          newSelected = [...prev.selectedCards, cardId];
        }

        // Auto-transition to await_play when 2+ cards selected
        const newPhase = newSelected.length >= 2 ? 'await_play' : 'guide_select';
        return { ...prev, selectedCards: newSelected, phase: newPhase as TutorialPhase };
      });
    },
    [engineState.phase],
  );

  // ── Selection change handler (for HandSelector) ────────────────────────────

  const handleSelectionChange = useCallback(
    (ids: string[]) => {
      if (engineState.phase !== 'guide_select' && engineState.phase !== 'await_play') return;

      setEngineState((prev) => {
        const newPhase = ids.length >= 2 ? 'await_play' : 'guide_select';
        return { ...prev, selectedCards: ids, phase: newPhase as TutorialPhase };
      });
    },
    [engineState.phase],
  );

  // ── Play hand handler ───────────────────────────────────────────────────────

  const handlePlayHand = useCallback(
    (cardIds: string[]) => {
      if (engineState.phase !== 'await_play') return;

      setEngineState((prev) => {
        const playedCards = prev.currentHand.filter((c) => cardIds.includes(c.id));
        if (playedCards.length < 1) return prev;

        // Compute modifiers from jokers
        const combo = resolveCombo(playedCards);
        const mods = jokerModifiers(prev.playerJokers, combo.comboId, prev.playerHearts);
        const breakdown = calculateScore(playedCards, mods);
        const newScore = prev.playerScore + breakdown.total;

        // Award chips based on score
        const chipsEarned = Math.round(breakdown.total * 0.15);

        const turn: TurnResolution = {
          sequence: prev.timeline.length + 1,
          player: TUTORIAL_PLAYER_WALLET,
          cardsPlayed: playedCards,
          modifiers: [],
          breakdown,
        };

        soundManager.play('powerup');
        markMechanicLearned('cards');
        markMechanicLearned('turns');

        // Check victory
        if (newScore >= TARGET_SCORE) {
          incrementCompleted();
          soundManager.play('win');
          return {
            ...prev,
            playerScore: newScore,
            playerChips: prev.playerChips + chipsEarned,
            timeline: [...prev.timeline, turn],
            lastPlayerBreakdown: breakdown,
            handsPlayed: prev.handsPlayed + 1,
            selectedCards: [],
            currentHand: [],
            phase: 'victory' as TutorialPhase,
            isCompleted: true,
            winner: 'player',
            turnNumber: prev.turnNumber + 1,
          };
        }

        return {
          ...prev,
          playerScore: newScore,
          playerChips: prev.playerChips + chipsEarned,
          timeline: [...prev.timeline, turn],
          lastPlayerBreakdown: breakdown,
          handsPlayed: prev.handsPlayed + 1,
          selectedCards: [],
          currentHand: [],
          phase: 'show_score' as TutorialPhase,
          turnNumber: prev.turnNumber + 1,
        };
      });
    },
    [engineState.phase, markMechanicLearned, incrementCompleted],
  );

  // ── Shop handlers ──────────────────────────────────────────────────────────

  const handlePurchase = useCallback(async (offerId: string) => {
    setEngineState((prev) => {
      const offer = prev.shopOffers.find((o) => o.offerId === offerId);
      if (!offer || offer.kind !== 'joker') return prev;
      if (prev.playerChips < offer.joker.cost) return prev;
      if (prev.playerJokers.length >= 5) return prev;

      soundManager.play('powerup');
      return {
        ...prev,
        playerChips: prev.playerChips - offer.joker.cost,
        playerJokers: [...prev.playerJokers, offer.joker],
        shopOffers: prev.shopOffers.filter((o) => o.offerId !== offerId),
        phase: 'explain_joker' as TutorialPhase,
        jokerExplained: true,
      };
    });
  }, []);

  const handleShopReady = useCallback(async () => {
    soundManager.play('click');
    setEngineState((prev) => ({ ...prev, phase: 'shop_done' as TutorialPhase }));
  }, []);

  // ── Coach dismiss handler ──────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    soundManager.play('click');
    setEngineState((prev) => {
      switch (prev.phase) {
        case 'welcome':
          return dealHand(prev);
        case 'guide_select':
        case 'await_play':
          setCoachDismissedForPhase(prev.phase);
          return prev;
        case 'show_score':
          return { ...prev, phase: 'ai_thinking' as TutorialPhase };
        case 'ai_scored':
          return afterAiScored(prev);
        case 'explain_hearts':
          return dealHand(prev);
        case 'shop_intro':
          return { ...prev, phase: 'shop_active' as TutorialPhase };
        case 'explain_joker':
          return { ...prev, phase: 'shop_active' as TutorialPhase };
        default:
          return prev;
      }
    });
  }, [dealHand, afterAiScored]);

  // ── Skip all tips ───────────────────────────────────────────────────────────

  const handleSkipAll = useCallback(() => {
    skipAllTips();
    soundManager.play('click');
  }, [skipAllTips]);

  // ── Play again ──────────────────────────────────────────────────────────────

  const handlePlayAgain = useCallback(() => {
    setEngineState(createInitialState());
  }, []);

  // ── Coach configuration per phase ──────────────────────────────────────────

  const getCoachConfig = useCallback(
    (st: EngineState): CoachConfig | null => {
      if (tipsSkipped) return null;

      switch (st.phase) {
        case 'welcome':
          return {
            title: 'Welcome to Your Match!',
            body: `You're playing against a Rookie Bot. Reach ${TARGET_SCORE} points to win. You have ${STARTING_HEARTS} hearts and ${STARTING_CHIPS} starting chips. No real SOL at stake — this is purely for learning!`,
            category: 'gameplay',
          };

        case 'guide_select': {
          if (!st.bestPlay) return null;
          const comboName = st.bestPlay.breakdown.combo.name;
          const cards = cardListStr(st.bestPlay.cards);
          const isFirst = st.handsPlayed === 0;
          const isSecond = st.handsPlayed === 1;

          let body: string;
          if (isFirst) {
            body = `Your hand has a ${comboName}! Select these cards: ${cards}`;
          } else if (isSecond) {
            body = `Look for the strongest combo in your hand. Hint: there's a ${comboName} hiding in there!`;
          } else if (st.playerJokers.length > 0 && st.handsPlayed === SHOP_AFTER_HANDS) {
            body = `Find your best combo! Your joker${st.playerJokers.length > 1 ? 's' : ''} will add bonus points. (Hint: ${comboName})`;
          } else {
            body = `Find your best combo! (Hint: ${comboName})`;
          }

          return {
            title: 'Select Your Cards',
            body,
            category: 'gameplay',
            highlightSelector: '[data-tutorial="game-canvas"]',
            requiredAction: 'Select at least 2 cards',
          };
        }

        case 'await_play': {
          // Check if player selected the optimal combo
          if (st.bestPlay) {
            const optimalIds = new Set(st.bestPlay.cards.map((c) => c.id));
            const selectedCards = st.currentHand.filter((c) => st.selectedCards.includes(c.id));
            const selectedBreakdown = calculateScore(selectedCards);
            const isOptimal =
              st.selectedCards.every((id) => optimalIds.has(id)) &&
              st.selectedCards.length === st.bestPlay.cards.length;

            if (isOptimal) {
              return {
                title: 'Perfect Selection!',
                body: `Great combo! Now hit PLAY to submit your hand.`,
                category: 'gameplay',
              };
            } else if (selectedBreakdown.total > 0) {
              return {
                title: 'Play Your Hand!',
                body: `That works! But ${st.bestPlay.breakdown.combo.name} (${cardListStr(st.bestPlay.cards)}) would score higher. PLAY when ready!`,
                category: 'gameplay',
              };
            }
          }
          return {
            title: 'Play Your Hand!',
            body: 'Hit the PLAY button (or press Space) to submit your hand.',
            category: 'gameplay',
          };
        }

        case 'show_score': {
          if (!st.lastPlayerBreakdown) return null;
          const { combo, total, modifiers } = st.lastPlayerBreakdown;
          const modText =
            modifiers.length > 0
              ? ` + ${modifiers.map((m) => `${m.name}: +${m.bonus}`).join(', ')}`
              : '';
          return {
            title: `${combo.name}!`,
            body: `Scored ${total} points! (${combo.score} base x ${combo.multiplier}x${modText})`,
            category: 'scoring',
          };
        }

        case 'ai_thinking':
          return null; // No coach — just the turn announcement

        case 'ai_scored': {
          if (!st.lastAiBreakdown) return null;
          return {
            title: "Opponent's Turn",
            body: `AI played ${st.lastAiBreakdown.combo.name} for ${st.lastAiBreakdown.total} points. Players alternate turns each round.`,
            category: 'gameplay',
          };
        }

        case 'explain_hearts':
          return {
            title: 'Hearts & Failure',
            body: `You have ${st.playerHearts} hearts. Scoring 0 on a hand (no valid combo) costs 1 heart. Lose all hearts = instant defeat. Always play at least a pair!`,
            category: 'gameplay',
          };

        case 'shop_intro': {
          const cheapest = [...st.shopOffers]
            .filter((o): o is ShopOffer & { kind: 'joker' } => o.kind === 'joker')
            .sort((a, b) => a.joker.cost - b.joker.cost)[0];
          const jokerHint = cheapest
            ? ` ${cheapest.joker.name} costs ${cheapest.joker.cost} chips — ${describeJokerEffect(cheapest.joker.effects[0])}.`
            : '';
          return {
            title: 'The Shop is Open!',
            body: `Spend your chips on jokers (permanent power-ups).${jokerHint} Buy a joker or click Ready to continue.`,
            category: 'shop',
            highlightSelector: '[data-tutorial="shop-panel"]',
          };
        }

        case 'shop_active':
          return null; // Player interacts with shop freely

        case 'explain_joker': {
          const lastJoker = st.playerJokers[st.playerJokers.length - 1];
          if (!lastJoker) return null;
          const effectDesc = lastJoker.effects
            .map((e: JokerEffect) => describeJokerEffect(e))
            .join(', ');
          return {
            title: 'Joker Equipped!',
            body: `${lastJoker.name} gives ${effectDesc} every hand! Joker effects stack and activate automatically.`,
            category: 'shop',
          };
        }

        case 'victory':
          return null; // Result modal handles this

        default:
          return null;
      }
    },
    [tipsSkipped],
  );

  // ── Determine coach visibility ──────────────────────────────────────────────

  const coachConfig = getCoachConfig(engineState);
  const showCoach =
    coachConfig !== null && !tipsSkipped && coachDismissedForPhase !== engineState.phase;

  // For action gating: coach is "completed" when required action is met
  const coachActionCompleted =
    engineState.phase === 'guide_select'
      ? engineState.selectedCards.length >= 2
      : engineState.phase === 'await_play'
        ? true // Always allow play
        : false;

  // ── Build ClientMatchState for GameCanvas/HandSelector ─────────────────────

  const buildClientMatchState = useCallback((st: EngineState): ClientMatchState => {
    const isPlayerTurn = st.phase === 'guide_select' || st.phase === 'await_play';
    const isShop = st.phase === 'shop_active' || st.phase === 'shop_intro';

    const playerRunState = {
      chips: st.playerChips,
      hearts: st.playerHearts,
      totalScore: st.playerScore,
      jokerSlots: 5,
      jokers: st.playerJokers,
      consumables: [],
      consumablesUsed: [],
      pendingScorePercent: 0,
      tempoStreak: 0,
      phase: (isShop ? 'shop' : 'play') as 'play' | 'shop',
      nextShopTurn: st.shopVisited ? 999 : SHOP_AFTER_HANDS * 2,
      shopGeneration: st.shopVisited ? 1 : 0,
      shopRerolls: 0,
    };

    const aiRunState = {
      chips: 0,
      hearts: STARTING_HEARTS,
      totalScore: st.aiScore,
      jokerSlots: 5,
      jokers: [],
      consumables: [],
      consumablesUsed: [],
      pendingScorePercent: 0,
      tempoStreak: 0,
      phase: 'play' as const,
      nextShopTurn: 999,
      shopGeneration: 0,
      shopRerolls: 0,
    };

    const shopState: RunShopState | undefined =
      isShop || st.phase === 'shop_done'
        ? { offers: st.shopOffers, rerollCost: 50, generation: 1 }
        : undefined;

    return {
      matchId: 'tutorial-' + st.handSequence,
      wager: { currency: 'SOL', amountLamports: '0' },
      players: [
        { wallet: TUTORIAL_PLAYER_WALLET, displayName: 'You', avatarSeed: TUTORIAL_PLAYER_WALLET },
        { wallet: TUTORIAL_AI_WALLET, displayName: 'AI (Rookie)', avatarSeed: TUTORIAL_AI_WALLET },
      ],
      targetScore: TARGET_SCORE,
      currentTurnWallet: isPlayerTurn ? TUTORIAL_PLAYER_WALLET : TUTORIAL_AI_WALLET,
      timeline: st.timeline,
      currentHand: st.currentHand.length > 0 ? st.currentHand : undefined,
      currentHandOwner: st.currentHand.length > 0 ? TUTORIAL_PLAYER_WALLET : undefined,
      currentHandSequence: st.handSequence,
      chatMessages: [],
      playerRunStates: {
        [TUTORIAL_PLAYER_WALLET]: playerRunState,
        [TUTORIAL_AI_WALLET]: aiRunState,
      },
      playerShopStates: shopState ? { [TUTORIAL_PLAYER_WALLET]: shopState } : undefined,
      phase: isShop ? 'shop' : 'play',
      tempoState: {
        [TUTORIAL_PLAYER_WALLET]: 0,
        [TUTORIAL_AI_WALLET]: 0,
      },
    };
  }, []);

  const clientMatchState = buildClientMatchState(engineState);
  const isMyTurn = engineState.phase === 'guide_select' || engineState.phase === 'await_play';
  const isShopPhase = engineState.phase === 'shop_active' || engineState.phase === 'shop_intro';

  return {
    state: engineState,
    currentPhase: engineState.phase,
    coachConfig,
    showCoach,
    coachActionCompleted,
    clientMatchState,
    isMyTurn,
    isShopPhase,
    selectedCards: engineState.selectedCards,
    handleCardClick,
    handlePlayHand,
    handleDismiss,
    handleSkipAll,
    handlePurchase,
    handleShopReady,
    handlePlayAgain,
    handleSelectionChange,
  };
}
